/**
 * Client HTTP pour les API Backend Agbaza (profil Établissement).
 * - Préfixe toutes les requêtes avec VITE_API_BASE_URL + '/api'
 * - Ajoute Authorization: Bearer <accessToken> quand un access token est présent (stocké en mémoire)
 * - Sur 401 : tente POST /auth/etablissement/refresh-token (cookie HttpOnly)
 *   puis rejoue la requête initiale.
 */

const AUTH_BASE = "/auth/etablissement";
const PRO_AUTH_BASE = "/auth/professionnel";
const PATIENT_AUTH_BASE = "/auth/patient";

const onUnauthorizedCallbacks = new Set<() => void>();
const onIdentityUnverifiedCallbacks = new Set<(message: string) => void>();

// Access token stocké uniquement en mémoire (migration auth “server-side persistence”).
let _token: string | null = null;

export function setUnauthorizedCallback(cb: () => void) {
  onUnauthorizedCallbacks.add(cb);
}

/**
 * S'abonne aux 403 `IDENTITE_NON_VERIFIEE` (compte non vérifié) pour afficher une
 * bannière/redirection générique — voir doc FRONTEND sur la restriction sous validation
 * de compte. Les endpoints `access/request-otp` / `access/verify-otp` s'en excluent via
 * `skipIdentityUnverifiedHandling` car ils affichent déjà le message backend en contexte.
 */
export function setIdentityUnverifiedCallback(cb: (message: string) => void) {
  onIdentityUnverifiedCallbacks.add(cb);
}

export function getStoredToken(): string | null {
  return _token;
}

/** JWT nu pour le header Authorization (sans préfixe "Bearer "). */
function bareJwtFromStored(stored: string): string {
  const t = stored.trim();
  return /^bearer\s+/i.test(t) ? t.replace(/^bearer\s+/i, "").trim() : t;
}

export function setStoredToken(token: string | null) {
  _token = token ? token.trim() : null;
}

function getBaseUrl(): string | null {
  const base = import.meta.env.VITE_API_BASE_URL;
  if (!base || typeof base !== "string") {
    return null;
  }
  return base.replace(/\/$/, "") + "/api";
}

export type ApiFetchOptions = RequestInit & {
  skipAuth?: boolean;
  /** Si true, un 401 ne déclenche pas onUnauthorized (utilisé par initAuth pour éviter déco au chargement). */
  silent401?: boolean;
  /** Si true, un 403 IDENTITE_NON_VERIFIEE ne déclenche pas la bannière/redirection globale
   * (utilisé par le flux OTP d'accès dossier, qui affiche déjà le message backend en contexte). */
  skipIdentityUnverifiedHandling?: boolean;
};

async function doRefresh(authBase: string): Promise<string | null> {
  const baseUrl = getBaseUrl();
  if (!baseUrl) return null;
  // Cookie HttpOnly refreshToken : le navigateur l’envoie automatiquement grâce à `credentials: "include"`.

  const url = baseUrl + authBase + "/refresh-token";
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    // Refresh KO => on vide le token mémoire conformément au plan.
    setStoredToken(null);
    return null;
  }

  const payload = (await res.json().catch(() => null)) as unknown;

  // Backend peut renvoyer le token sous des structures imbriquées différentes
  // (ex: data.token, data.data.token, ou accessToken à la place de token).
  function extractToken(node: unknown): string | null {
    let foundAccessToken: string | null = null;
    let foundToken: string | null = null;

    const visit = (n: unknown) => {
      if (typeof foundAccessToken === "string" && foundAccessToken.trim()) return;
      if (n == null) return;

      if (typeof n === "object") {
        if (Array.isArray(n)) {
          for (const item of n) visit(item);
          return;
        }

        const o = n as Record<string, unknown>;
        const a = o.accessToken;
        if (typeof a === "string" && a.trim()) {
          foundAccessToken = a.trim();
          return;
        }
        const t = o.token;
        if (typeof t === "string" && t.trim() && !foundToken) {
          foundToken = t.trim();
        }

        // Descendre dans les clés pour gérer `data.data.token` etc.
        for (const k of Object.keys(o)) {
          // Evite les cycles évidents.
          visit(o[k]);
          if (foundAccessToken) return;
        }
      }
    };

    visit(node);
    return foundAccessToken ?? foundToken;
  }

  const newToken = extractToken(payload);
  if (typeof newToken === "string" && newToken.trim()) {
    setStoredToken(newToken);
    return newToken;
  }

  // Refresh OK mais backend ne fournit pas d'access token.
  setStoredToken(null);
  return null;
}

const API_NOT_CONFIGURED_MESSAGE =
  "Backend non configuré. Définissez VITE_API_BASE_URL dans un fichier .env à la racine du projet.";

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<
  | { data: T; ok: true }
  | { ok: false; status: number; message: string; errorCode?: string; meta?: Record<string, unknown> }
> {
  const baseUrl = getBaseUrl();
  if (!baseUrl) {
    return { ok: false, status: 0, message: API_NOT_CONFIGURED_MESSAGE };
  }
  const { skipAuth = false, silent401 = false, skipIdentityUnverifiedHandling = false, ...init } = options;
  const url = path.startsWith("http") ? path : baseUrl + (path.startsWith("/") ? path : "/" + path);
  const token = skipAuth ? null : getStoredToken();
  const MINISTERE_AUTH_BASE = "/auth/ministere";
  const authBase =
    path.includes(PRO_AUTH_BASE) || path.startsWith("/professionnel")
      ? PRO_AUTH_BASE
      : path.includes(PATIENT_AUTH_BASE) || path.startsWith("/patient/")
        ? PATIENT_AUTH_BASE
        : path.includes(MINISTERE_AUTH_BASE) || path.startsWith("/ministere/")
          ? MINISTERE_AUTH_BASE
          : AUTH_BASE;
  const refreshEndpoint = authBase + "/refresh-token";
  const isRefreshEndpoint = path === refreshEndpoint || url.endsWith(refreshEndpoint);
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(init.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${bareJwtFromStored(token)}`;

  let res: Response;
  try {
    res = await fetch(url, { ...init, headers, credentials: "include" });
  } catch (err) {
    const msg =
      err instanceof TypeError && (err as Error).message?.includes("fetch")
        ? "Backend indisponible. Vérifiez que le serveur API est démarré."
        : (err as Error)?.message ?? "Erreur réseau";
    return { ok: false, status: 0, message: msg };
  }

  if (res.status === 401 && !skipAuth && !isRefreshEndpoint) {
    const newToken = await doRefresh(authBase);
    if (newToken) {
      headers.Authorization = `Bearer ${bareJwtFromStored(newToken)}`;
      res = await fetch(url, { ...init, headers, credentials: "include" });
    }
    if (res.status === 401) {
      if (!silent401) {
        // Log utile pour identifier la requete qui déclenche un clearAuth.
        if (import.meta.env.DEV) {
          console.warn("[auth] 401 non-silencieux, fallback refresh a échoué:", { path, url });
        }
        onUnauthorizedCallbacks.forEach((cb) => cb());
      }
      return { ok: false, status: 401, message: "Session expirée" };
    }
  }

  const text = await res.text();
  let data: T;
  try {
    data = text ? (JSON.parse(text) as T) : (undefined as T);
  } catch {
    data = text as unknown as T;
  }

  if (!res.ok) {
    const d = data as Record<string, unknown>;
    let message =
      (typeof d?.message === "string" && d.message) ||
      (typeof d?.error === "string" && d.error) ||
      `Erreur ${res.status}`;
    const errs = d?.errors;
    if (Array.isArray(errs) && errs.length > 0) {
      const parts = errs.map((e: unknown) => {
        if (typeof e === "string") return e;
        if (e && typeof e === "object") {
          const o = e as Record<string, unknown>;
          const bit = [o.path, o.field, o.msg, o.message].filter((x) => x != null && String(x) !== "");
          return bit.length ? bit.join(": ") : JSON.stringify(e);
        }
        return String(e);
      });
      message = `${String(message)} — ${parts.join("; ")}`;
    }
    const meta = d?.meta as Record<string, unknown> | undefined;
    const errorCode = typeof meta?.errorCode === "string" ? meta.errorCode : undefined;
    if (res.status === 403 && errorCode === "IDENTITE_NON_VERIFIEE" && !skipIdentityUnverifiedHandling) {
      onIdentityUnverifiedCallbacks.forEach((cb) => cb(String(message)));
    }
    return { ok: false, status: res.status, message: String(message), errorCode, meta };
  }
  return { data, ok: true };
}
